import type { HiLoApi } from '@goodtime/engine';
import type { AnyPlayView, FirstPlayRequest, FriendId, PlayOffer, RingCellKind } from '@goodtime/shared';
import { api } from './client';
import { actPlay, declinePlay, fetchPlay, getPlay } from './plays';
import { withRetry } from './road';

/** A lamp's free first game: by its friend on a road, or by the lamp
 *  itself on a Wheel Run slot (whose lamps have no friend). */
export type FirstPlayLamp = { friend: FriendId } | { lamp: RingCellKind };

/** Open a lamp's free first game (or reopen it while it's being played).
 *  The server answers 409 once it's been used, or before the lamp is lit. */
export function startFirstPlay(sessionId: string, themeId: string, which: FirstPlayLamp): Promise<PlayOffer> {
  const name = 'friend' in which ? which.friend : which.lamp;
  const body: FirstPlayRequest = { themeId, ...which };
  return api<PlayOffer>(`/sessions/${encodeURIComponent(sessionId)}/board/first-play`, {
    method: 'POST',
    headers: { 'Idempotency-Key': `first-${name}-${themeId}-${sessionId.slice(0, 8)}` },
    body: JSON.stringify(body),
  });
}

/** A play as an offer, to open its game again (a lamp's game after a reload). */
export async function offerOfPlay(playId: string, sessionId: string): Promise<PlayOffer> {
  const view = await fetchPlay<AnyPlayView>(playId, sessionId);
  return {
    playId: view.playId,
    kind: view.kind,
    base: view.base,
    maxValue: view.maxValue,
    origin: view.origin,
    creditTo: view.creditTo,
    setup: view.setup,
  };
}

/** The calls a friend's Hi-Lo (Willow's) makes; the same as the bonus's,
 *  without its beacons. */
export function hiloApi(playId: string, sessionId: string): HiLoApi {
  return {
    act: (step, action, arg) => withRetry(() => actPlay(playId, { sessionId, step, action, arg })),
    decline: () => declinePlay(playId, sessionId).then(() => undefined),
    recover: async () => {
      const play = await getPlay(playId, sessionId).catch(() => null);
      if (!play) return null;
      return { step: play.step, state: play.state, finished: play.status === 'done' || play.status === 'expired', payout: play.payout };
    },
  };
}
